// ============================================================
// Mobile "active project" rail — minimal: just the names; the active one sits
// in a small soft-accent pill (no underline, no label, no dot; seit 09/2026
// fuer Admins genau EIN Pin vorneweg — der Weg zur Gesamtkarte /uebersicht,
// die es auf dem Telefon sonst nirgends gaebe). Sticky
// with a small top gap so the row never clips. On desktop this is hidden — the
// switcher lives in the top bar instead.
//
// Dazu wohnen hier die VORGRIFFS-Helfer der App (siehe unten) — TopNav und
// CommandBar holen sie sich von hier.
// ============================================================
import type { CSSProperties } from "react";
import { useEffect, useRef } from "react";
import { useQueryClient, type FetchQueryOptions, type QueryClient } from "@tanstack/react-query";
import { api } from "../api/http";
import { load, save } from "../lib/storage";
import { useNavigate } from "react-router-dom";
import { Icon } from "../components/ui";
import { useStore } from "../store/useStore";

// ------------------------------------------------------------
// Vorgriff — aufwaermen beim ZEIGEN, nicht erst beim Klicken.
//
// Zwischen Zeigerkontakt und Klick liegen typisch 200-600 ms. Der Server
// beantwortet project/stats/layers in 17-24 ms; wer diese Spanne nutzt, hat die
// Zielansicht beim Klick fertig im Zwischenspeicher und zeigt gar keinen
// Ladezustand mehr — die Latenzleiter kennt unter 100 ms keinen.
//
// Warum die Helfer ausgerechnet HIER stehen und nicht in einem eigenen Modul:
// TopNav und CommandBar brauchen dieselben, und ProjectSwitcher ist das einzige
// Blatt unter ihnen (TopNav laedt CommandBar, CommandBar niemanden von beiden).
// Von hier aus entsteht kein Ringschluss.
//
// Auf Beruehrungsgeraeten feuert kein Zeigerkontakt. Der Vorgriff ist dort
// wirkungslos — aber nicht schaedlich: es passiert schlicht nichts.
// ------------------------------------------------------------

/**
 * react-query 5.102 markiert prefetchQuery/fetchQuery/ensureQueryData als
 * veraltet und stellt `queryClient.query(options)` an ihre Stelle. Welche der
 * beiden die installierte Fassung wirklich mitbringt, wird zur LAUFZEIT
 * gefragt statt zur Bauzeit geraten: ein Vorgriff — reine Bequemlichkeit —
 * darf niemals der Grund sein, warum die App nach einem Paketwechsel klemmt.
 */
type MitQuery = { query?: (opts: FetchQueryOptions) => Promise<unknown> };

function warm(qc: QueryClient, opts: FetchQueryOptions): void {
  const neu = (qc as unknown as MitQuery).query;
  const lauf = typeof neu === "function" ? neu.call(qc, opts) : qc.prefetchQuery(opts);
  // Ein gescheiterter Vorgriff ist ein Nicht-Ereignis: der echte Aufruf beim
  // Klick meldet den Fehler dann an der Stelle, an der er hingehoert.
  void Promise.resolve(lauf).catch(() => undefined);
}

/**
 * Die drei Abfragen, von denen jede Projektansicht lebt, warm ziehen.
 * `mitStats` ist die teuerste und ausserhalb des Dashboards ueberfluessig —
 * die Karte braucht sie nicht.
 */
export function warmProjekt(qc: QueryClient, id: string | null | undefined, mitStats = true): void {
  if (!id) return;
  warm(qc, { queryKey: ["project", id], queryFn: () => api.projects.get(id) });
  warm(qc, { queryKey: ["layers", id], queryFn: () => api.projects.layers(id) });
  if (mitStats) warm(qc, { queryKey: ["stats", id], queryFn: () => api.projects.stats(id) });
}

/**
 * Den Chunk einer Zielansicht anwaermen.
 *
 * Die Modulkennungen muessen WOERTLICH zu denen in App.tsx passen (dort als
 * lazy()-Import) — Vite buendelt nach aufgeloester Kennung, ein Tippfehler
 * zoege einen zweiten Chunk statt denselben vorzuziehen.
 *
 * Fehler werden geschluckt: ein 404 auf einen Chunk laesst App.tsx
 * (retryOnStaleShell) die Seite neu laden — beim blossen Darueberfahren waere
 * genau das das falsche Ereignis.
 */
export function warmRouteChunk(ziel: "stats" | "karte" | "admin" | "konto"): void {
  if (ziel === "stats") void import("../screens/dashboard/ProjectStats").catch(() => undefined);
  else if (ziel === "karte") void import("../screens/karte/KarteScreen").catch(() => undefined);
  else if (ziel === "admin") void import("../screens/admin/AdminScreen").catch(() => undefined);
  else void import("../screens/AccountScreen").catch(() => undefined);
}

export function ProjectSwitcher({
  projects,
  currentId,
  onPick,
  style,
}: {
  projects: { id: string; name: string }[];
  currentId: string;
  onPick: (id: string) => void;
  style?: CSSProperties;
}) {
  const kennung = useStore((st) => st.auth?.id ?? st.auth?.username ?? "anon");
  const istAdmin = useStore((st) => st.auth?.role === "admin");
  const navigate = useNavigate();
  const schluessel = `skelettRail.${kennung}`;
  const qc = useQueryClient();
  const leisteRef = useRef<HTMLDivElement>(null);
  const mehrere = !!projects && projects.length >= 2;
  // Hinweis fuers Lade-Skelett: hat DIESER Nutzer eine Projektschiene? Ein
  // Einzelprojekt-Konto bekommt dann auch kein Schienen-Skelett vorgezeichnet.
  // Der Merker haengt an der KENNUNG, nicht am Browser: sonst erbte der
  // naechste Angemeldete am selben Rechner die Schiene des vorigen.
  useEffect(() => {
    if (load<boolean | null>(schluessel, null) !== mehrere) save(schluessel, mehrere);
  }, [schluessel, mehrere]);
  /* Und WIE die Schiene aussieht — Pillenhoehe und die Breite jeder Pille.
     Das „ob" allein reicht der Silhouette nicht: sie zeichnet bisher drei
     Pillen zu 96/92/84 px und 34 px Hoehe, waehrend hier in Wirklichkeit
     28 px hohe Pillen stehen, deren Breite am PROJEKTNAMEN haengt (gemessen,
     claude-test bei 390 px: Anstecker 35, dann 100/93/82/164/147). Namen kann
     man nicht raten, aber nachschlagen — dieselbe Selbstvermessung wie bei den
     Seitenmassen (lib/skelettMass).
     VERTRAG fuer den Leser (components/SeitenSkelett, .skl-rail):
       railMass.<kennung> = { h: Hoehe einer Pille, p: [Breiten in Reihenfolge] }
     Fehlt der Eintrag, gelten wie bisher die einkodierten Rueckfallbreiten. */
  useEffect(() => {
    if (!mehrere) return;
    const el = leisteRef.current;
    if (!el) return;
    const id = window.setTimeout(() => {
      void (document.fonts?.ready ?? Promise.resolve()).then(() => {
        const knoepfe = [...(leisteRef.current?.querySelectorAll<HTMLElement>("button") ?? [])];
        // Auf dem Schreibtisch ist die Schiene display:none — alles 0. Dann
        // gilt „nicht gesehen", nicht „keine Pillen".
        const p = knoepfe.map((b) => Math.round(b.getBoundingClientRect().width));
        const h = Math.round(knoepfe[0]?.getBoundingClientRect().height ?? 0);
        if (!h || !p.length || p.some((x) => !x)) return;
        const key = `railMass.${kennung}`;
        const alt = load<{ h: number; p: number[] } | null>(key, null);
        if (alt && alt.h === h && alt.p.length === p.length && alt.p.every((x, i) => Math.abs(x - p[i]!) <= 1)) return;
        save(key, { h, p });
      });
    }, 400);
    return () => window.clearTimeout(id);
  }, [mehrere, kennung, projects.length, istAdmin]);
  if (!mehrere) return null;
  return (
    /* data-skl="rail": Messanker fuer das Lade-Skelett. Die Hoehe der Schiene
       haengt an den Schriftmetriken und an der Zahl der Projekte — sie gehoert
       gemessen, nicht geraten. Auf dem Schreibtisch ist die Schiene
       display:none, das Rechteck also 0x0, und die Messung traegt sauber 0. */
    <div className="psw-rail" data-skl="rail" style={{ position: "sticky", top: 8, zIndex: 30, ...style }}>
      <div ref={leisteRef} className="no-scrollbar" style={{ display: "flex", gap: 6, overflowX: "auto", padding: "2px 2px 4px" }}>
        {/* Vorgriff: der Fingerdruck hat keine Vorlaufzeit, der Tabulator schon
            — deshalb zaehlt hier vor allem onFocus. Der Chunk ist an beiden
            Einsatzorten (Dashboard, Admin) laengst geladen, es fehlen nur die
            Daten des Zielprojekts. */}
        {istAdmin && (
          <button
            onClick={() => navigate("/uebersicht")}
            className="ring-focus"
            title="Übersicht — alle Kommunen"
            aria-label="Übersicht — alle Kommunen"
            style={{
              flex: "none",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              background: "var(--panel-solid)",
              border: "1px solid var(--field-border)",
              borderRadius: 9,
              padding: "5px 9px",
              cursor: "pointer",
              color: "var(--accent-text)",
              transition: "background .15s ease, border-color .15s ease",
            }}
          >
            <Icon name="map-pin-area" size={15} weight="fill" />
          </button>
        )}
        {projects.map((p) => {
          const on = p.id === currentId;
          return (
            <button
              key={p.id}
              onClick={() => onPick(p.id)}
              onMouseEnter={() => warmProjekt(qc, p.id)}
              onFocus={() => warmProjekt(qc, p.id)}
              className="ring-focus"
              aria-current={on ? "true" : undefined}
              style={{
                flex: "none",
                // fully-opaque pills: active = solid accent (white text), inactive = solid surface
                background: on ? "var(--accent)" : "var(--panel-solid)",
                border: `1px solid ${on ? "var(--accent)" : "var(--field-border)"}`,
                borderRadius: 9,
                padding: "5px 12px",
                cursor: "pointer",
                fontSize: 14,
                fontWeight: on ? 700 : 600,
                color: on ? "#fff" : "var(--text-dim)",
                whiteSpace: "nowrap",
                transition: "background .15s ease, color .15s ease, border-color .15s ease",
              }}
            >
              {p.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
